import type {OnCompileContext, ObjectPath, TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {MaterialAssignment} from "#template/nodes/material-assignment/material-assignment"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {IMaterialData, MeshRenderSettings} from "@cm/material-nodes"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {MeshDecal} from "#template/nodes/object/mesh-decal"
import type {MaterialLikeNode} from "#template/nodes/types"
import {EMPTY_LIST} from "#template/runtime-graph/types"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"

const materialMapValueDesc = TypeDescriptors.Nullable(TypeDescriptors.MaterialData)

export type MaterialAssignmentsParameters = {
    [x: string]: MaterialAssignment | null
}

const materialAssignmentsDefinition: TemplateNodeDefinition<MaterialAssignmentsParameters> = {
    nodeClass: "MaterialAssignments",
    validation: {
        parameters: z.record(z.string(), z.nullable(graphNode(MaterialAssignment))),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MaterialAssignments extends TemplateNode<MaterialAssignmentsParameters> {
    public override get definition() {
        return materialAssignmentsDefinition
    }

    setupMaterialAssignments(
        scope: GraphBuilderScope,
        context: OnCompileContext,
        path: BuilderInlet<ObjectPath>,
        meshRenderSettings: BuilderInlet<MeshRenderSettings> | undefined,
    ): {materialMap: BuilderOutlet<Map<number, IMaterialData | null>>; preDisplayList: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[]>} {
        const assignmentEntries = Object.entries(this.parameters).map(([key, assignment]) => {
            return [
                parseInt(key),
                setupMaterialAssignmentWithOverride(scope.scope(key), context, scope, path, {materialAssignments: this, key}, assignment, meshRenderSettings),
            ] as const
        })

        const materialMapList = scope.list(
            assignmentEntries.map(([key, value]) => scope.tuple(key, value?.materialData ?? null)),
            "matAssignmentList",
        )
        const preDisplayList = scope.filterInvalid(
            scope.sparseMerge(
                assignmentEntries.map(([, value]) => value?.preDisplayList ?? null),
                `materialMapMerge`,
            ),
        )

        return {materialMap: scope.entriesToMap(materialMapList, materialMapValueDesc, "materialMap"), preDisplayList}
    }
}

const temporaryAssignment = new MaterialAssignment({parameters: {node: null, side: "front"}})

export function setupMaterialAssignmentWithOverride(
    scope: GraphBuilderScope,
    context: OnCompileContext,
    parentScope: GraphBuilderScope,
    path: BuilderInlet<ObjectPath>,
    parent: MeshDecal | {materialAssignments: MaterialAssignments; key: string},
    materialAssignment: MaterialAssignment | null,
    meshRenderSettings: BuilderInlet<MeshRenderSettings> | undefined,
) {
    const {overrideMaterial} = context

    const nullRet: {
        materialData: BuilderOutlet<IMaterialData | null>
        preDisplayList: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[]>
    } = {
        materialData: scope.valueWithoutType(null, "nullMaterialData"),
        preDisplayList: scope.valueWithoutType(EMPTY_LIST, "nullPreDisplayList"),
    }

    if (overrideMaterial === undefined)
        return materialAssignment === null ? nullRet : materialAssignment.setupMaterialAssignment(scope, context, parentScope, path, meshRenderSettings)

    const overriddenMaterial: MaterialLikeNode | null | undefined = overrideMaterial(parent)
    if (overriddenMaterial === undefined)
        return materialAssignment === null ? nullRet : materialAssignment.setupMaterialAssignment(scope, context, parentScope, path, meshRenderSettings)
    else {
        if (materialAssignment) return materialAssignment.setupMaterialAssignment(scope, context, parentScope, path, meshRenderSettings, overriddenMaterial)

        if (overriddenMaterial === null) return nullRet

        return temporaryAssignment.setupMaterialAssignment(scope, context, parentScope, path, meshRenderSettings, overriddenMaterial)
    }
}
